import React, { useState, memo } from 'react';
import { Copy, Check, X } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import TextField from './helpers/TextField';

const subtitleClass = 'text-sm font-bold text-ninja-dark dark:text-white mb-3';

const Woocommerce = ({ ecommerce, warehouses, onSave, loading, saving = false, isEdit }) => {
  const [formData, setFormData] = useState(ecommerce);
  const [deliveryInput, setDeliveryInput] = useState("");
  const [copiedTooltip, setCopiedTooltip] = useState("");

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        authentication: {
          ...prev.configuration.authentication,
          [field]: value,
        },
      },
    }));
  };

  const handleWarehouseChange = (value) => {
    setFormData({ ...formData, warehouse: value });
  };

  const addDeliveryMethod = () => {
    if (!deliveryInput) return;
    const currentMethods =
      formData.configuration.options.enabled_delivery_methods || [];
    if (!currentMethods.includes(deliveryInput)) {
      setFormData((prev) => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          options: {
            ...prev.configuration.options,
            enabled_delivery_methods: [...currentMethods, deliveryInput],
          },
        },
      }));
    }
    setDeliveryInput("");
  };

  const removeDeliveryMethod = (method) => {
    const currentMethods =
      formData.configuration.options.enabled_delivery_methods.filter(
        (m) => m !== method,
      );
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        options: {
          ...prev.configuration.options,
          enabled_delivery_methods: currentMethods,
        },
      },
    }));
  };

  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedTooltip(fieldName);
      setTimeout(() => setCopiedTooltip(""), 2000);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const renderCopy = (text, fieldName, label) => (
    <button
      type="button"
      onClick={() => handleCopy(text, fieldName)}
      title={copiedTooltip === fieldName ? "¡Copiado!" : "Copiar"}
      aria-label={`Copiar ${label}`}
      className="p-1.5 rounded-lg text-text-secondary hover:text-ninja-green transition-colors"
    >
      {copiedTooltip === fieldName ? <Check size={16} /> : <Copy size={16} />}
    </button>
  );

  const deliveryMethods = formData.configuration.options.enabled_delivery_methods || [];

  return (
    <form data-testid="ecommerce-form" data-platform="woocommerce">
      <h3 className="text-lg font-bold text-ninja-dark dark:text-white mb-5">Configuración WooCommerce</h3>

      <div className="mb-5">
        <FilterableSelect
          variant="form"
          testId="ecommerce-warehouse"
          label="Bodega Asociada"
          value={formData.warehouse || ""}
          onChange={handleWarehouseChange}
          options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
          placeholder="Seleccionar..."
        />
      </div>

      <div className="space-y-4 mb-5">
        <p className={subtitleClass}>Credenciales API</p>
        <TextField
          label="Usuario"
          id="woocommerce_user"
          name="woocommerce_user"
          value={formData.configuration.authentication.woocommerce_user || ""}
          onChange={(e) => handleChange("woocommerce_user", e.target.value)}
        />
        <TextField
          label="Contraseña"
          id="woocommerce_password"
          name="woocommerce_password"
          type="text"
          value={formData.configuration.authentication.woocommerce_password || ""}
          onChange={(e) => handleChange("woocommerce_password", e.target.value)}
        />
        <TextField
          label="Token"
          id="token"
          name="token"
          value={formData.configuration.authentication.token || ""}
          onChange={(e) => handleChange("token", e.target.value)}
        />
        <TextField
          label="Refresh Token"
          id="refresh_token"
          name="refresh_token"
          value={formData.configuration.authentication.refresh_token || ""}
          onChange={(e) => handleChange("refresh_token", e.target.value)}
        />
      </div>

      <div className="h-px bg-border my-6" />

      <p className={subtitleClass}>Webhooks</p>

      <div className="space-y-4 mb-5">
        <TextField
          label="Webhook Crear Orden"
          value={formData.configuration.create_order?.url || ""}
          disabled
          endAdornment={renderCopy(formData.configuration.create_order?.url, "create", "Webhook Crear Orden")}
        />
        <TextField
          label="Webhook Crear Orden con Plugin Ninja"
          value={formData.configuration.create_order_plugin?.url || ""}
          disabled
          endAdornment={renderCopy(formData.configuration.create_order_plugin?.url, "plugin", "Webhook Crear Orden con Plugin Ninja")}
        />
        <TextField
          label="Webhook Cancelar Orden"
          value={formData.configuration.cancel_order?.url || ""}
          disabled
          endAdornment={renderCopy(formData.configuration.cancel_order?.url, "cancel", "Webhook Cancelar Orden")}
        />
        <TextField
          label="Webhook Actualizar Orden"
          value={formData.configuration.update_order?.url || ""}
          disabled
          endAdornment={renderCopy(formData.configuration.update_order?.url, "update", "Webhook Actualizar Orden")}
        />
      </div>

      <div data-testid="ecommerce-delivery-methods" className="mt-8 mb-5 border border-border rounded-xl p-4">
        <span className="block text-sm font-semibold text-ninja-dark dark:text-white mb-2">
          Métodos de Envío Habilitados
        </span>
        <div className="flex items-center gap-2 mb-3">
          <TextField
            className="flex-1"
            aria-label="Método de envío"
            placeholder="Ej: Starken, Chilexpress..."
            value={deliveryInput}
            onChange={(e) => setDeliveryInput(e.target.value)}
          />
          <Button type="button" variant="primary" onClick={addDeliveryMethod} disabled={!deliveryInput}>
            Agregar
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {deliveryMethods.length > 0 ? (
            deliveryMethods.map((m) => (
              <span
                key={m}
                data-testid="ecommerce-delivery-method"
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-ninja-green text-ninja-green text-sm"
              >
                {m}
                <button type="button" aria-label={`Quitar ${m}`} onClick={() => removeDeliveryMethod(m)} className="hover:text-danger">
                  <X size={14} />
                </button>
              </span>
            ))
          ) : (
            <span className="text-sm text-text-muted">No hay métodos configurados</span>
          )}
        </div>
      </div>

      <Button
        type="submit"
        variant="ghost"
        size="lg"
        fullWidth
        disabled={loading}
        loading={saving}
        loadingLabel={isEdit ? "Guardando..." : "Finalizando..."}
        onClick={handleSubmit}
      >
        {isEdit ? "Guardar cambios de configuración" : "Finalizar integración"}
      </Button>
    </form>
  );
};

export default memo(Woocommerce);
